import { useCallback, useEffect, useState } from "react";
import {
    AlertColor,
    Box,
    Button,
    Paper,
    Table,
    TableContainer,
    TablePagination,
    Tabs,
    Tab,
    Tooltip,
    Typography,
} from "@mui/material";
import { FormAlert, FormSnackbar } from "../../../../components/Shared/Form";
import { useCampaignsDataStore, usePaymentStore } from "../../../../config/advertiser.stores";
import LoadingOverlay from "../../../../components/Shared/LoadingOverlay/index";
import RefreshButton from "../../../../components/Shared/RefreshButton";
import adService from "../../../../services/ad.service";
import authService from "../../../../services/auth.service";
import CreateCampaign from "../../../../components/Advertiser/CreateCampaign";
import Header from "../../../../components/Advertiser/Campaigns/Header";
import Rows from "../../../../components/Advertiser/Campaigns/Rows";
import { useUser } from "../../../../hooks/useUser";

const Campaigns = () => {
    const [tab, setTab] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openNewCampaign, setOpenNewCampaign] = useState(false);

    const user = useUser();

    const [alert, setAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");
    const [alertId, setAlertId] = useState<number | null>(null);

    const [snackbar, setSnackbar] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    const campaignsData = useCampaignsDataStore((state) => state.campaignsData);
    const setCampaignsData = useCampaignsDataStore((state) => state.setCampaignsData);
    const paymentStatus = usePaymentStore((state) => state.paymentStatus);
    const setPaymentStatus = usePaymentStore((state) => state.setPaymentStatus);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const response = await adService.query();
        setIsLoading(false);
        setCampaignsData(response);
    }, [setCampaignsData]);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        const response = await authService.status(user.id);
        setIsLoading(false);
        setPaymentStatus(response);
    }, [user.id, setPaymentStatus]);

    const getCampaignsForTable = useCallback(() => {
        if (tab === 0) {
            return campaignsData.campaigns.filter((campaign) => campaign.adStatus === "active");
        } else if (tab === 1) {
            return campaignsData.campaigns.filter((campaign) => ["pending approval", "rejected"].includes(campaign.adStatus ?? ""));
        } else if (tab === 2) {
            return campaignsData.campaigns.filter((campaign) => campaign.adStatus === null);
        } else if (tab === 3) {
            return campaignsData.campaigns.filter((campaign) => campaign.adStatus === "disabled");
        }
        return [];
    }, [tab, campaignsData])

    async function handleSubmitAd(adId: number) {
        setIsLoading(true);
        const response = await adService.submit(adId);
        if (response) {
            fetchData();
            setSeverity("success");
            setSnackbar("Ad has been submitted for review.");
        } else {
            setIsLoading(false);
            setSeverity("error");
            setSnackbar("There was an error submitting the ad.");
        }
        setAlert(false);
    }

    async function handleDisableAd(adId: number) {
        setIsLoading(true);
        const response = await adService.updateDisabled(adId);
        if (response) {
            fetchData();
            setSeverity("success");
            setSnackbar("Ad has been closed.");
        } else {
            setIsLoading(false);
            setSeverity("error");
            setSnackbar("There was an error closing the ad.");
        }
        setAlert(false);
    }

    async function handleNullAd(adId: number) {
        setIsLoading(true);
        const response = await adService.updateNull(adId);
        if (response) {
            fetchData();
            setSeverity("success");
            setSnackbar("Ad is no longer submitted for review.");
        } else {
            setIsLoading(false);
            setSeverity("error");
            setSnackbar("There was an error cancelling the ad's review.");
        }
        setAlert(false);
    }

    const showAlert = (message: string, type: string, adId: number) => {
        setAlertMessage(message);
        setAlertType(type);
        setAlertId(adId);
        setAlert(true);
    }

    const getAlertHandleAgree = () => {
        if (alertType && alertId) {
            return () => {
                return alertType === "submit" ? (
                    handleSubmitAd(alertId)
                ) : alertType === "cancel" ? (
                    handleNullAd(alertId)
                ) : (
                    handleDisableAd(alertId)
                );
            }
        }
    }
    
    useEffect(() => {
        if (campaignsData.campaigns.length === 0) { 
            fetchData();
        }
        fetchStatus();
        // eslint-disable-next-line
    }, []);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
        setPage(0);
    };

    const createTable = (adStatus: string) => {
        const data = getCampaignsForTable();
        
        return (
            <>
                <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                    <Table sx={{ minWidth: 1100 }}>
                        <Header adStatus={adStatus} />
                        <Rows
                            data={data}
                            adStatus={adStatus}
                            page={page}
                            showAlert={showAlert}
                            fetchData={fetchData}
                        />
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.length ?? 0}
                    rowsPerPage={5}
                    rowsPerPageOptions={[]}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                />
            </>
        )
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                gap: 3,
                margin: 3,
            }}
        >
            <LoadingOverlay open={isLoading} />
            <Box display="flex" justifyContent="space-between" sx={{ alignItems: "center", position: 'relative' }}>
                <Typography variant="h5">Manage Campaigns</Typography>
                <Box position='absolute' right={0}>
                    <Tooltip
                        title={paymentStatus !== "active" ? 
                            "You must first add a payment method at Settings > Billing & Usage to create a campaign" : 
                            ""}
                    >
                        <span>
                            <Button
                                variant="contained"
                                sx={{ marginRight: 1 }}
                                onClick={() => { setOpenNewCampaign(true) }}
                                disabled={paymentStatus !== "active"}
                            >
                                Create New Campaign
                            </Button>
                        </span>
                    </Tooltip>
                    <RefreshButton onRefresh={fetchData} disabled={isLoading} />
                </Box>
                <CreateCampaign
                    fetchData={fetchData}
                    open={openNewCampaign}
                    setOpen={setOpenNewCampaign}
                />
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange}>
                    <Tab label="Active" id="active-tab" aria-controls="active-tabpanel"/>
                    <Tab label="Pending Approval" id="review-tab" aria-controls="review-tabpanel"/>
                    <Tab label="Drafts" id="draft-tab" aria-controls="draft-tabpanel"/>
                    <Tab label="Completed" id="past-tab" aria-controls="past-tabpanel"/>
                </Tabs>
            </Box>
            <div role="tabpanel" hidden={tab !== 0} id="active-tabpanel" aria-labelledby="active-tab">
                {tab === 0 && createTable("active")}
            </div>
            <div role="tabpanel" hidden={tab !== 1} id="review-tabpanel" aria-labelledby="review-tab">
                {tab === 1 && createTable("pendingApproval")}
            </div>
            <div role="tabpanel" hidden={tab !== 2} id="draft-tabpanel" aria-labelledby="draft-tab">
                {tab === 2 && createTable("draft")}
            </div>
            <div role="tabpanel" hidden={tab !== 3} id="past-tabpanel" aria-labelledby="past-tab">
                {tab === 3 && createTable("past")}
            </div>
            <FormAlert
                label="campaign"
                open={alert}
                setOpen={setAlert}
                description={alertMessage}
                disabled={isLoading}
                handleAgree={getAlertHandleAgree()}
                agreeText={
                    alertType === "submit" ? (
                        "Submit for Review"
                    ) : alertType === "cancel" ? (
                        "Cancel Review"
                    ) : (
                        "Move Ad to Completed"
                    )
                }
            />
            <FormSnackbar message={snackbar} setMessage={setSnackbar} severity={severity} />
        </Box>
    );
}

export default Campaigns;
